import React, { memo, useState, useEffect, useMemo } from 'react';
import * as Sift from '@sift/types';
import * as Types from '../typings';
import * as Entity from 'pages/Pages/typings';
import classnames from 'classnames';
import _split from 'lodash/split';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import _includes from 'lodash/includes';
import { useSelector } from 'react-redux';
import { useFormikContext } from 'formik';
import getPriorityListItems from 'helpers/getPriorityListItems';

import PriorityList from 'pages/SiftBot/components/PriorityList';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import Switch from '@material-ui/core/Switch';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';

import makeStyles from '@material-ui/core/styles/makeStyles';
import convertStringToArrayOfStrings from 'helpers/convertStringToArrayOfStrings';
import { PERSON_ENTITY_TYPE_KEY } from 'helpers/useEntityTypes';

const OPTIONS_TYPES = ['textSelect', 'multiSelect'];
const CHIP_TYPES = ['autocompleteTextCollection', 'textCollection'];

interface Props {
  attribute: Sift.Field;
  onChange: (field: string, value: any) => void;
  values: Types.AttributesEditFormValues;
  entityType: Entity.EntityType;
}

function AttributeUserEditing({ attribute, onChange, entityType, values }: Props) {
  const { resetForm, errors } = useFormikContext();
  const classes = useStyles();
  const _client = useSelector((state: any) => state.client);
  const [options, setOptions] = useState<string | null>(null);
  const [priorityModalOpen, setPriorityModalOpen] = useState(false);
  const { _fields } = entityType;
  const valueReadOnly = _get(values, 'client.valueReadOnly', false);
  const disabledClass = classnames({
    [classes.disabled]: valueReadOnly,
  });
  const profileCompletionEnabled = _get(_client, 'settings.siftBot.enabled', false);
  const isRequiredAttribute = useMemo(
    () =>
      profileCompletionEnabled &&
      values.priorityList &&
      values.priorityList.some(item => item.objectKey === attribute.objectKey),
    [values, attribute, profileCompletionEnabled],
  );
  const priorityList = _get(values, 'priorityList');
  const priorityListItems = getPriorityListItems(_fields, priorityList);

  const shouldRenderRequiredAttribute = useMemo(
    () =>
      (entityType.typeKey === PERSON_ENTITY_TYPE_KEY &&
        priorityListItems.some(item => item.objectKey === attribute.objectKey)) ||
      (priorityList && priorityList.some(item => item.objectKey === attribute.objectKey)),
    [],
  );
  const shouldRenderOptions = OPTIONS_TYPES.includes(attribute.baseField);
  const shouldRenderMaxNumberOfChips = CHIP_TYPES.includes(attribute.baseField);
  const shouldRenderAutocompleteFields =
    _get(attribute, 'baseField') === 'autocompleteTextCollection';
  const shouldRenderMaxLinks = _get(attribute, 'baseField') === 'linkCollection';
  const shouldRenderNumberRange = _get(attribute, 'baseField') === 'number';
  const shouldRenderAnyCardContent =
    shouldRenderRequiredAttribute ||
    shouldRenderOptions ||
    shouldRenderMaxNumberOfChips ||
    shouldRenderAutocompleteFields ||
    shouldRenderMaxLinks ||
    shouldRenderNumberRange;

  const requiredAttributeCopy = (
    <>
      If enabled, people will be reminded to fill in the information when it’s empty. To add to your
      priority list,
    </>
  );

  const getOptions = () => {
    if (attribute.baseField === 'textSelect') {
      return _get(attribute, 'validation._selectOptions', [])
        .map((option: { [value: string]: string }) => option.value)
        .join(', ');
    } else if (attribute.baseField === 'multiSelect') {
      return _get(attribute, 'item.validation._selectOptions', [])
        .map((option: { [value: string]: string }) => option.value)
        .join(', ');
    }
  };

  useEffect(() => {
    if (attribute.baseField === 'textSelect' || attribute.baseField === 'multiSelect') {
      const initialOptions = getOptions();
      setOptions(initialOptions);
      if (attribute.baseField === 'textSelect') {
        onChange(
          'client.validation._selectOptions',
          convertOptionsToArrayOfObjects(initialOptions),
        );
      }
      if (attribute.baseField === 'multiSelect') {
        onChange(
          'client.item.validation._selectOptions',
          convertOptionsToArrayOfObjects(initialOptions),
        );
      }
    }
  }, []);

  useEffect(() => {
    if (attribute.baseField === 'textSelect') {
      let currentValues = _get(values, 'client.validation._selectOptions');
      if (!currentValues) {
        setOptions(null);
        onChange('client.validation._selectOptions', undefined);
      } else if (!_isEqual(currentValues, convertOptionsToArrayOfObjects(options))) {
        setOptions(
          currentValues.map((option: { [value: string]: string }) => option.value).join(', '),
        );
        onChange('client.validation._selectOptions', currentValues);
      }
    }
    if (attribute.baseField === 'multiSelect') {
      let currentValues = _get(values, 'client.item.validation._selectOptions');
      if (!currentValues) {
        setOptions(null);
        onChange('client.item.validation._selectOptions', undefined);
      } else if (!_isEqual(currentValues, convertOptionsToArrayOfObjects(options))) {
        setOptions(
          currentValues.map((option: { [value: string]: string }) => option.value).join(', '),
        );
        onChange('client.item.validation._selectOptions', currentValues);
      }
    }
  }, [values]);

  const handleAutocompleteOptionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    onChange('autocomplete', value);
  };

  const handleChangePriorityList = (newFields: Sift.Field[]) => {
    onChange('priorityList', newFields);
  };

  const handleSelectOptionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    let value = e.target.value;
    setOptions(value);
    if (attribute.baseField === 'textSelect') {
      onChange('client.validation._selectOptions', convertOptionsToArrayOfObjects(value));
    }
    if (attribute.baseField === 'multiSelect') {
      onChange('client.item.validation._selectOptions', convertOptionsToArrayOfObjects(value));
    }
  };

  const handleFalseySwitchChange = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    onChange(key, !e.target.checked);
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    onChange(key, e.target.checked);
  };

  const handleMinMaxChange = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    let value = e.target.value;
    onChange(key, Number(value));
  };

  return (
    <>
      <Card className={classes.cardRoot}>
        <CardHeader
          title="User Editing Experience"
          subheader="If switched off, users will never be able to edit this value. If switched on, the user will be able to edit if it is not mapped to a data source."
          action={
            <Switch
              checked={!valueReadOnly}
              inputProps={{ 'aria-label': 'user editing switch' }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFalseySwitchChange(e, 'client.valueReadOnly')
              }
            />
          }
        />
        {shouldRenderAnyCardContent && <Divider />}
        {shouldRenderAnyCardContent && (
          <CardContent className={disabledClass}>
            {shouldRenderRequiredAttribute && (
              <div className={classes.content}>
                <div>
                  <Typography variant="h6">Required Attribute</Typography>
                  <Typography variant="subtitle2">
                    {requiredAttributeCopy}
                    <Button
                      className={classes.positionButton}
                      disabled={valueReadOnly}
                      onClick={() => {
                        setPriorityModalOpen(true);
                      }}
                    >
                      click here.
                    </Button>
                  </Typography>
                </div>
                <div className={classes.subAction}>
                  <Switch
                    checked={isRequiredAttribute}
                    inputProps={{ 'aria-label': 'required switch' }}
                    disabled={true}
                  />
                </div>
              </div>
            )}
            {shouldRenderOptions && (
              <div
                className={classnames({
                  [classes.marginTop]: shouldRenderRequiredAttribute,
                })}
              >
                <Typography variant="h6">Options</Typography>
                <Typography variant="subtitle2">
                  What are the options that people can choose from? Separate options by commas or
                  line breaks. The maximum number of options is 200.
                </Typography>
                <TextField
                  id="attribute-options"
                  disabled={valueReadOnly}
                  value={options || ''}
                  onChange={handleSelectOptionsChange}
                  inputProps={{ 'aria-label': 'options input' }}
                  multiline
                  fullWidth
                  style={{ maxWidth: 450 }}
                  error={!!_get(errors, 'selectOptions')}
                  helperText={_get(errors, 'selectOptions')}
                />
              </div>
            )}
            {shouldRenderMaxNumberOfChips && (
              <div className={classnames({ [classes.marginTop]: shouldRenderRequiredAttribute })}>
                <div>
                  <Typography variant="h6">Maximum Number of Chips</Typography>
                  <Typography variant="subtitle2">
                    The maximum number of items that may be entered by users.
                  </Typography>
                </div>
                <TextField
                  id="attribute-max-number-of-chips"
                  disabled={valueReadOnly}
                  value={_get(values, 'client.validation.maxLength', 0)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleMinMaxChange(e, 'client.validation.maxLength')
                  }
                  inputProps={{ 'aria-label': 'max chips input' }}
                  style={{ maxWidth: 450 }}
                  type="number"
                />
              </div>
            )}
            {shouldRenderAutocompleteFields && (
              <>
                <div
                  className={classnames({
                    [classes.marginTop]:
                      shouldRenderRequiredAttribute || shouldRenderMaxNumberOfChips,
                  })}
                >
                  <Typography variant="h6">Autocomplete Database</Typography>
                  <Typography variant="subtitle2">
                    What are the values that autocomplete? Separate options by spaces, commas, or
                    line breaks. The max number of options are 2000.
                  </Typography>
                  <TextField
                    id="autocomplete options"
                    disabled={valueReadOnly}
                    value={values.autocomplete || ''}
                    onChange={handleAutocompleteOptionsChange}
                    inputProps={{ 'aria-label': 'autocomplete options' }}
                    multiline={true}
                    fullWidth={true}
                  />
                </div>
                <div className={classnames(classes.content, classes.marginTop)}>
                  <div>
                    <Typography variant="h6">Add Free-Form Entries to Database</Typography>
                    <Typography variant="subtitle2">
                      If enabled, free-form entries will be added to the autocomplete database for
                      future input. This helps to reduce variations of one value.
                    </Typography>
                  </div>
                  <div className={classes.subAction}>
                    <Switch
                      checked={_get(values, 'client.autocomplete.addValuesAsEntries', false)}
                      inputProps={{ 'aria-label': 'allow new entries switch' }}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleSwitchChange(e, 'client.autocomplete.addValuesAsEntries')
                      }
                    />
                  </div>
                </div>
              </>
            )}
            {shouldRenderMaxLinks && (
              <div className={classes.marginTop}>
                <Typography variant="h6">Max Number of Links</Typography>
                <Typography variant="subtitle2">The default max number of links is 20.</Typography>
                <TextField
                  disabled={valueReadOnly}
                  id="attribute-max-number-of-links"
                  inputProps={{ 'aria-label': 'max number of links input' }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleMinMaxChange(e, 'client.validation.maxLength')
                  }
                  style={{ maxWidth: 450 }}
                  type="number"
                  value={_get(values, 'client.validation.maxLength')}
                />
              </div>
            )}
            {shouldRenderNumberRange && (
              <div className={classes.marginTop}>
                <Typography variant="h6">Number Range</Typography>
                <div className={classes.inline}>
                  <Typography className={classes.marginRight} variant="body2">
                    From{' '}
                  </Typography>
                  <TextField
                    className={classes.marginRight}
                    disabled={valueReadOnly}
                    id="min-number"
                    inputProps={{ 'aria-label': 'min number' }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleMinMaxChange(e, 'client.validation.min')
                    }
                    style={{ maxWidth: 220 }}
                    type="number"
                    value={_get(values, 'client.validation.min')}
                  />
                  <Typography className={classes.marginRight} variant="body2">
                    to{' '}
                  </Typography>
                  <TextField
                    className={classes.marginRight}
                    disabled={valueReadOnly}
                    id="max-number"
                    inputProps={{ 'aria-label': 'max number' }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleMinMaxChange(e, 'client.validation.max')
                    }
                    style={{ maxWidth: 220 }}
                    type="number"
                    value={_get(values, 'client.validation.max')}
                  />
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>
      {shouldRenderRequiredAttribute && (
        <PriorityList
          configFields={_fields}
          editPriorityList={handleChangePriorityList}
          isModalOpen={priorityModalOpen}
          onCloseModal={() => {
            setPriorityModalOpen(false);
          }}
          priorityList={_get(values, 'priorityList', [])}
          resetState={resetForm}
        />
      )}
    </>
  );
}

const useStyles = makeStyles((theme: any) => ({
  root: {},
  cardRoot: {
    maxWidth: 900,
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  marginRight: {
    marginRight: theme.spacing(1.5),
  },
  marginTop: {
    marginTop: theme.spacing(2),
  },
  inline: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  subAction: {
    flex: '0 0 auto',
    alignSelf: 'flex-start',
    marginTop: -theme.spacing(),
    marginRight: -theme.spacing(),
  },
  disabled: {
    opacity: theme.palette.opacity.thirtyEightPercent,
  },
  positionButton: {
    textTransform: 'none',
    color: theme.palette.secondary.main,
    marginBottom: theme.spacing(0.5),
  },
  link: {
    textDecoration: 'underline',
  },
}));

export default memo(AttributeUserEditing);

const convertOptionsToArrayOfObjects = (value: string | null | undefined) => {
  let options = convertStringToArrayOfStrings(value);
  return options ? options.map(option => ({ value: option })) : null;
};
